import { format } from 'date-fns';
import { useState, useRef, useEffect, type CSSProperties, type SVGProps, type FC } from 'react';
import { createPortal } from 'react-dom';
import './utcTooltip.css';

const UTCTooltip: FC<{ template: string }> = ({ template }) => {
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: number;
    top: number;
    arrowLeft: number;
    placeAbove: boolean;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null!);
  const triggerRef = useRef<HTMLSpanElement>(null!);

  const switchTooltip = () => {
    setTooltipIsOpen((prev) => !prev);
  };

  const now = new Date();
  let date: Date;

  if (template.includes('{{utc}}')) {
    date = new Date(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds()
    );
  } else {
    date = now;
  }

  const utcDate = format(date, 'h:mmaa, EEEE, MMMM d, yyyy');
  const localDate = format(now, 'h:mmaa, EEEE, MMMM d, yyyy');

  useEffect(() => {
    if (!tooltipIsOpen) {
      return;
    }
    const tooltip = tooltipRef.current;
    const trigger = triggerRef.current;
    if (!tooltip || !trigger) return;

    const tooltipRect = tooltip.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
    let top = triggerRect.top - tooltipRect.height - 10;
    let placeAbove = true;

    if (left + tooltipRect.width > window.innerWidth) {
      left = window.innerWidth - tooltipRect.width - 10;
    }
    if (left < 10) {
      left = 10;
    }
    if (top < 10) {
      top = triggerRect.bottom + 10;
      placeAbove = false;
    }

    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    let arrowLeft = triggerCenter - left - 10;
    arrowLeft = Math.max(10, Math.min(arrowLeft, tooltipRect.width - 20));

    setTooltipPosition({ left, top, arrowLeft, placeAbove });
  }, [tooltipIsOpen]);

  const tooltipStyle: CSSProperties = {
    left: tooltipPosition ? `${tooltipPosition.left}px` : undefined,
    top: tooltipPosition ? `${tooltipPosition.top}px` : undefined,
    opacity: tooltipIsOpen ? 1 : 0,
    pointerEvents: tooltipIsOpen ? 'auto' : 'none',
  };

  const arrowStyle: CSSProperties = tooltipPosition
    ? tooltipPosition.placeAbove
      ? {
          left: `${tooltipPosition.arrowLeft}px`,
          bottom: '-10px',
          transform: 'rotate(180deg)',
        }
      : {
          left: `${tooltipPosition.arrowLeft}px`,
          top: '-10px',
          transform: 'rotate(0deg)',
        }
    : {};

  if (template.length === 0) {
    return <></>;
  }

  const tooltipContent = template
    .replace(/\{\{local\}\}/g, localDate)
    .replace(/\{\{utc\}\}/g, utcDate)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<\/?p>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();

  const tooltipContentElement = <span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>;

  const tooltipOverlay =
    typeof document === 'undefined'
      ? null
      : createPortal(
          <>
            <div
              className="tooltip-mask"
              style={{ display: tooltipIsOpen ? 'block' : 'none' }}
              onClick={() => setTooltipIsOpen(false)}
            />
            <div ref={tooltipRef} className="tooltip utc-tooltip" style={tooltipStyle}>
              {tooltipContentElement}
              <CloseIcon className="tooltip-close" onClick={() => setTooltipIsOpen(false)} />
              <div className="tooltip-arrow" style={arrowStyle}>
                <svg width="20" height="10">
                  <polygon points="0,10 10,0 20,10" />
                </svg>
              </div>
            </div>
          </>,
          document.body
        );

  return (
    <>
      <div className="tooltip-wrapper">
        <div className="tooltip-trigger">
          <span className="tooltip-trigger-text">Times based on UTC. </span>
          <span ref={triggerRef} onClick={switchTooltip} className="tooltip-trigger-icon">
            <QuestionMarkIcon />
          </span>
        </div>
      </div>
      {tooltipOverlay}
    </>
  );
};

const QuestionMarkIcon: FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9.5" fill="none" stroke="currentColor" strokeWidth="1" />
    <text
      x="10"
      y="11.5"
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize="14"
      fill="currentColor"
      fontFamily="Arial"
      fontWeight="bold"
    >
      ?
    </text>
  </svg>
);

const CloseIcon: FC<SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <line
      x1="4"
      y1="4"
      x2="12"
      y2="12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line
      x1="12"
      y1="4"
      x2="4"
      y2="12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export default UTCTooltip;
